import { GameState, Position } from '../types/GameTypes';

// Define the events we'll be using
export enum WebSocketEvents {
  UPDATE_GAME_STATE = 'updateGameState',
  PLACE_FOOD = 'placeFood',
  CLEAN_POOP = 'cleanPoop',
  KICK_BALL = 'kickBall',
  REQUEST_GAME_STATE = 'requestGameState',
  DRAGON_EAT = 'dragonEat',
  DRAGON_KICK_BALL = 'dragonKickBall',
  DRAGON_MOVE = 'dragonMove',
  XP_GAIN = 'xpGain',
  DRAGON_DIED = 'dragonDied',
  DRAGON_RESPAWNED = 'dragonRespawned',
  // Auto-Feature Events
  TOGGLE_AUTO_FEATURE = 'toggleAutoFeature', // Client -> Server
  AUTO_CLEAN_ACTIVATE = 'autoCleanActivate', // Server -> Client
  AUTO_PLAY_ACTIVATE = 'autoPlayActivate',  // Server -> Client
  AUTO_FEED_ACTIVATE = 'autoFeedActivate',  // Server -> Client
}

// Message format
interface Message {
  event: string;
  data?: any;
}

// XP gain data format
export interface XPGainData {
  amount: number;
  position: Position;
  action: string;
}

// Added: Payload for auto-clean activation
export interface AutoCleanActivatePayload {
  poopIds: string[];
}

// Callback types
type GameStateCallback = (gameState: GameState) => void;
type DragonEatCallback = () => void;
type DragonKickBallCallback = (position: Position) => void;
type DragonMoveCallback = (position: Position) => void;
type ConnectionCallback = () => void;
type XPGainCallback = (data: XPGainData) => void;
type DragonDiedCallback = () => void;
type DragonRespawnedCallback = () => void;
// Added: Auto-Feature callbacks
type AutoCleanActivateCallback = (data: AutoCleanActivatePayload) => void;
type AutoPlayActivateCallback = () => void;
type AutoFeedActivateCallback = () => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private gameStateCallback: GameStateCallback | null = null;
  private dragonEatCallback: DragonEatCallback | null = null;
  private dragonKickBallCallback: DragonKickBallCallback | null = null;
  private dragonMoveCallback: DragonMoveCallback | null = null;
  private xpGainCallback: XPGainCallback | null = null;
  private dragonDiedCallback: DragonDiedCallback | null = null;
  private dragonRespawnedCallback: DragonRespawnedCallback | null = null;
  // Added: Auto-Feature callback refs
  private autoCleanActivateCallback: AutoCleanActivateCallback | null = null;
  private autoPlayActivateCallback: AutoPlayActivateCallback | null = null;
  private autoFeedActivateCallback: AutoFeedActivateCallback | null = null;
  private connectCallback: ConnectionCallback | null = null;
  private disconnectCallback: ConnectionCallback | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectDelay: number = 1000;
  private url: string = '';
  
  // Connect to the WebSocket server
  connect(serverUrl: string = process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:8080/ws'): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) return;
    
    console.log(`Attempting to connect to WebSocket server at: ${serverUrl} (Attempt ${this.reconnectAttempts + 1})`);
    this.url = serverUrl;
    
    try {
      this.socket = new WebSocket(serverUrl);
      
      this.socket.onopen = this.onOpen.bind(this);
      this.socket.onclose = this.onClose.bind(this);
      this.socket.onerror = this.onError.bind(this);
      this.socket.onmessage = this.onMessage.bind(this);
    } catch (error) {
      console.error('Error creating WebSocket instance:', error);
      this.scheduleReconnect();
    }
  }
  
  private onOpen(): void {
    console.log('Connected to WebSocket server');
    // Reset reconnect attempts on successful connection
    this.reconnectAttempts = 0;
    
    if (this.connectCallback) this.connectCallback();
    // Request the initial game state
    this.requestGameState();
  }
  
  private onClose(event: CloseEvent): void {
    console.log(`Disconnected from WebSocket server. Code: ${event.code}, Reason: '${event.reason}', WasClean: ${event.wasClean}`);
    
    if (this.disconnectCallback) this.disconnectCallback();
    
    // Try to reconnect if the connection was closed abnormally
    if (event.code !== 1000) {
      this.scheduleReconnect();
    }
  }
  
  private onError(event: Event): void {
    console.error('WebSocket encountered an error:', event);
    // Add more specific logging if possible, though the Event object might be generic
    if (event instanceof ErrorEvent) {
      console.error(`WebSocket error details: Message: ${event.message}, Filename: ${event.filename}, Line: ${event.lineno}`);
    }
    // Note: The onClose event will likely fire immediately after this, triggering reconnect logic.
  }
  
  private onMessage(event: MessageEvent): void {
    try {
      const message: Message = JSON.parse(event.data);
      
      switch (message.event) {
        case WebSocketEvents.UPDATE_GAME_STATE:
          if (this.gameStateCallback && message.data) {
            this.gameStateCallback(message.data as GameState);
          }
          break;
          
        case WebSocketEvents.DRAGON_EAT:
          console.log('Dragon ate food!');
          if (this.dragonEatCallback) this.dragonEatCallback();
          break;
          
        case WebSocketEvents.DRAGON_KICK_BALL:
          console.log('Dragon kicked ball to:', message.data);
          if (this.dragonKickBallCallback && message.data) {
            this.dragonKickBallCallback(message.data as Position);
          }
          break;
          
        case WebSocketEvents.DRAGON_MOVE:
          console.log('Dragon moved to:', message.data);
          if (this.dragonMoveCallback && message.data) {
            this.dragonMoveCallback(message.data as Position);
          }
          break;
          
        case WebSocketEvents.XP_GAIN:
          console.log('XP gained:', message.data);
          if (this.xpGainCallback && message.data) {
            this.xpGainCallback(message.data as XPGainData);
          }
          break;
          
        case WebSocketEvents.DRAGON_DIED:
          console.warn('Dragon has died!');
          if (this.dragonDiedCallback) {
            this.dragonDiedCallback();
          }
          break;
          
        case WebSocketEvents.DRAGON_RESPAWNED:
          console.log('Dragon has respawned!');
          if (this.dragonRespawnedCallback) {
            this.dragonRespawnedCallback();
          }
          break;
        
        // Added: Handle Auto-Feature activation events
        case WebSocketEvents.AUTO_CLEAN_ACTIVATE:
          console.log('Auto-Clean activated!', message.data);
          if (this.autoCleanActivateCallback && message.data) {
            this.autoCleanActivateCallback(message.data as AutoCleanActivatePayload);
          }
          break;
        case WebSocketEvents.AUTO_PLAY_ACTIVATE:
          console.log('Auto-Play activated!');
          if (this.autoPlayActivateCallback) {
            this.autoPlayActivateCallback();
          }
          break;
        case WebSocketEvents.AUTO_FEED_ACTIVATE:
          console.log('Auto-Feed activated!');
          if (this.autoFeedActivateCallback) {
            this.autoFeedActivateCallback();
          }
          break;
      }
    } catch (error) {
      console.error('Error parsing message:', error);
    }
  }
  
  private scheduleReconnect(): void {
    // Don't schedule multiple reconnects
    if (this.reconnectTimer !== null) return;
    
    // Check if we've exceeded max reconnect attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached, giving up');
      return;
    }
    
    // Increment attempt counter
    this.reconnectAttempts++;
    
    // Schedule reconnect
    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${this.reconnectDelay}ms`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.url);
    }, this.reconnectDelay);
    
    // Increase delay for next attempt (exponential backoff)
    this.reconnectDelay = Math.min(30000, this.reconnectDelay * 1.5);
  }
  
  // Send a message to the server
  private sendMessage(event: WebSocketEvents, data?: any): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return;
    }
    
    const message: Message = { event };
    if (data !== undefined) {
      message.data = data;
    }
    
    this.socket.send(JSON.stringify(message));
  }
  
  // Disconnect from the WebSocket server
  disconnect(): void {
    if (!this.socket) return;
    
    // Clear reconnect timer if active
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Close the connection
    this.socket.close(1000, 'Client disconnecting');
    this.socket = null;
  }
  
  // Register callbacks for different events
  onGameStateUpdate(callback: GameStateCallback): void {
    this.gameStateCallback = callback;
  }
  
  onDragonEat(callback: DragonEatCallback): void {
    this.dragonEatCallback = callback;
  }
  
  onDragonKickBall(callback: DragonKickBallCallback): void {
    this.dragonKickBallCallback = callback;
  }
  
  onDragonMove(callback: DragonMoveCallback): void {
    this.dragonMoveCallback = callback;
  }
  
  onXPGain(callback: XPGainCallback): void {
    this.xpGainCallback = callback;
  }
  
  onDragonDied(callback: DragonDiedCallback): void {
    this.dragonDiedCallback = callback;
  }
  
  onDragonRespawned(callback: DragonRespawnedCallback): void {
    this.dragonRespawnedCallback = callback;
  }
  
  onAutoCleanActivate(callback: AutoCleanActivateCallback): void {
    this.autoCleanActivateCallback = callback;
  }

  onAutoPlayActivate(callback: AutoPlayActivateCallback): void {
    this.autoPlayActivateCallback = callback;
  }

  onAutoFeedActivate(callback: AutoFeedActivateCallback): void {
    this.autoFeedActivateCallback = callback;
  }
  
  onConnect(callback: ConnectionCallback): void {
    this.connectCallback = callback;
  }
  
  onDisconnect(callback: ConnectionCallback): void {
    this.disconnectCallback = callback;
  }
  
  // Send events to the server
  placeFood(): void {
    this.sendMessage(WebSocketEvents.PLACE_FOOD);
  }
  
  cleanPoop(id: string): void {
    this.sendMessage(WebSocketEvents.CLEAN_POOP, { id });
  }
  
  kickBall(): void {
    this.sendMessage(WebSocketEvents.KICK_BALL);
  }
  
  requestGameState(): void {
    this.sendMessage(WebSocketEvents.REQUEST_GAME_STATE);
  }
  
  dragonEatFood(): void {
    this.sendMessage(WebSocketEvents.DRAGON_EAT);
  }

  // Added: Send event to toggle an auto-feature
  toggleAutoFeature(feature: string, enabled: boolean): void {
    this.sendMessage(WebSocketEvents.TOGGLE_AUTO_FEATURE, { feature, enabled });
  }
}

// Create and export a singleton instance
const webSocketService = new WebSocketService();
export default webSocketService; 